import React, { useEffect } from 'react';
import { List } from 'antd';
import { CSSProperties, MutableRefObject, useRef } from 'react';
import classNames from 'classnames';
import _isFunction from 'lodash/isFunction';

interface SelectableListProps {
	items: any[];
	onSelect: (item: any) => void;
	renderValue?: (item: any) => any;
	/** Invokes onSelect with null value if selected item was clicked */
	allowDeselect?: boolean;
	className?: string;
	style?: CSSProperties;
	itemClassName?: string;
	itemStyle?: CSSProperties;

	// Use this in case the item is an object
	// This is for accessibility and will help querying list items
	// In case item is not an object and it's a string/number than it will be used instead
	labelKey?: string;
}

interface SelectableListItemProps {
	item: any;
	label?: string;
	renderValue: (item: any) => any;
	onItemSelected: (item: any, ref: MutableRefObject<HTMLDivElement>) => void;
	className: string;
	style: CSSProperties;
}

const SelectableListItem: React.FC<SelectableListItemProps> = ({
	item,
	renderValue,
	label,
	onItemSelected,
	className,
	style
}) => {
	const listItemRef = useRef<HTMLDivElement>(null);

	const handleOnClick = () => onItemSelected(item, listItemRef);

	let ariaLabel: string | undefined;

	if (typeof label === 'string') {
		ariaLabel = label;
	} else if (typeof item === 'string' || typeof item === 'number') {
		ariaLabel = item + '';
	}

	return (
		<div
			role="listitem"
			ref={listItemRef}
			onClick={handleOnClick}
			className={classNames('selectable-list-item', className)}
			style={style}
			aria-label={ariaLabel}
		>
			{_isFunction(renderValue) ? renderValue(item) : item}
		</div>
	);
};

const SelectableList: React.FC<SelectableListProps> = ({
	items,
	onSelect,
	className,
	style,
	itemClassName,
	itemStyle,
	renderValue,
	allowDeselect,
	labelKey
}) => {
	let selectItemRef = useRef<HTMLDivElement>(null);

	const handleItemRender = (className: string, style: CSSProperties = {}) => item => {
		return (
			<SelectableListItem
				label={
					typeof item?.[labelKey] === 'string' || item?.[labelKey] === 'number'
						? item?.[labelKey] + ''
						: undefined
				}
				item={item}
				renderValue={renderValue}
				onItemSelected={handleItemSelected}
				className={className}
				style={style}
			/>
		);
	};

	const handleItemSelected = (item, ref: MutableRefObject<HTMLDivElement>) => {
		if (selectItemRef.current) {
			const isSelected = ref?.current?.classList?.contains('selected');
			unSelectListItem();
			if (allowDeselect && isSelected) {
				onSelect(null);
				return;
			}
		}

		ref.current.classList.add('selected');
		selectItemRef.current = ref.current;
		onSelect(item);
	};

	const unSelectListItem = () => {
		selectItemRef?.current?.classList?.remove('selected');
	};

	const deselectCurrent = () => {
		onSelect(null);
		unSelectListItem();
	};

	useEffect(() => {
		allowDeselect && deselectCurrent();
	}, [items]);

	return (
		<List
			dataSource={items}
			renderItem={handleItemRender(itemClassName, itemStyle)}
			className={classNames('selectable-list', className)}
			style={style}
		/>
	);
};

export default SelectableList;
